/**
 * planVoiceOps.ts — строгая схема операций голосового конструктора плана.
 *
 * Единственный источник типов для:
 *   - фронтенд MIS (constructor-window/voice/)
 *   - фронтенд LK (treatment/voice/) — идентичная копия
 *   - backend MIS (ConstructorVoiceAdapter.normalize_llm_output)
 *   - AMI prompt (переменная {{ops_schema_hint}})
 *
 * ПРАВИЛО: не добавлять новые op без одновременного обновления:
 *   1. этого файла
 *   2. planVoicePromptSeed.ts (YAML-пример)
 *   3. ConstructorVoiceAdapter.normalize_llm_output (Python)
 *   4. opToHuman() ниже
 */

// ─── Базовые вложенные типы ───────────────────────────────────────────────────

export type VoiceCashOffer = {
  /** Цена в минорных единицах (копейки) */
  price_minor: number
  label: string | null
}

export type VoiceInstallmentConfig = {
  months: number
  rate_pct: number
  /** ISO date: YYYY-MM-DD */
  start_date: string
  /** Если null — берётся из плана при apply */
  total_minor: number | null
}

export type VoiceShareConfig = {
  active: boolean
  /** ISO datetime или null */
  expires_at: string | null
  online_payments: boolean
  allow_yandex_pay: boolean
  hide_prices: boolean
}

export type VoiceServiceRef = {
  /** ID плана-источника (в МИС — plan_id) */
  source_plan_id: string
  /** Конкретные snapshot_id, если AI нашёл точное совпадение */
  snapshot_ids: string[]
  /** Fallback: AI не нашёл точных ID — фильтр по категории */
  category_filter: string | null
  /** Количество */
  qty: number
}

// ─── Операции (discriminated union) ──────────────────────────────────────────

/** Добавить/создать этап плана */
export type OpAddStage = {
  op: "add_stage"
  /** Порядковый индекс (0-based); null = добавить в конец */
  stage_index: number | null
  name: string
  doctor_id: string | null
  comment: string | null
  cash_offer: VoiceCashOffer | null
  services: VoiceServiceRef[]
}

/** Скопировать услуги из другого плана в существующий этап */
export type OpCopyServices = {
  op: "copy_services"
  /** Целевой этап (0-based) */
  stage_index: number
  services: VoiceServiceRef[]
}

/** Настроить рассрочку для всего плана */
export type OpSetInstallment = {
  op: "set_installment"
  config: VoiceInstallmentConfig | null
}

/** Установить таймер оффера */
export type OpSetOfferDeadline = {
  op: "set_offer_deadline"
  /** ISO datetime или null (убрать таймер) */
  deadline: string | null
}

/** Установить специальную цену за наличные */
export type OpSetPlanCashOffer = {
  op: "set_plan_cash_offer"
  offer: VoiceCashOffer | null
}

/** Настроить шеринг плана пациенту */
export type OpSetSharing = {
  op: "set_sharing"
  config: VoiceShareConfig
}

/** Установить публичный комментарий к плану */
export type OpSetPlanComment = {
  op: "set_plan_comment"
  comment: string | null
}

/** Удалить этап */
export type OpRemoveStage = {
  op: "remove_stage"
  stage_index: number
}

/** Назначить врача на этап */
export type OpSetStageDoctorId = {
  op: "set_stage_doctor"
  stage_index: number
  doctor_id: string | null
}

export type PlanVoiceOp =
  | OpAddStage
  | OpCopyServices
  | OpSetInstallment
  | OpSetOfferDeadline
  | OpSetPlanCashOffer
  | OpSetSharing
  | OpSetPlanComment
  | OpRemoveStage
  | OpSetStageDoctorId

// ─── Верхнеуровневый payload ──────────────────────────────────────────────────

export type PlanVoiceCommand = {
  /** Версия схемы — проверяем при parse */
  schema_version: "1"
  /** AI-резюме всего что сказал врач (одно предложение) */
  summary: string
  operations: PlanVoiceOp[]
}

// ─── Runtime parse + guard ────────────────────────────────────────────────────

function isString(v: unknown): v is string {
  return typeof v === "string" && v.length > 0
}

function parseCashOffer(v: unknown): VoiceCashOffer | null {
  if (!v || typeof v !== "object") return null
  const o = v as Record<string, unknown>
  const price = typeof o.price_minor === "number" ? o.price_minor : null
  if (!price || price <= 0) return null
  return { price_minor: Math.round(price), label: typeof o.label === "string" ? o.label : null }
}

function parseInstallment(v: unknown): VoiceInstallmentConfig | null {
  if (!v || typeof v !== "object") return null
  const o = v as Record<string, unknown>
  const months = typeof o.months === "number" ? Math.round(o.months) : null
  const start_date = typeof o.start_date === "string" ? o.start_date : null
  if (!months || months < 1 || !start_date) return null
  return {
    months,
    rate_pct: typeof o.rate_pct === "number" ? o.rate_pct : 0,
    start_date,
    total_minor: typeof o.total_minor === "number" ? o.total_minor : null,
  }
}

function parseServiceRef(v: unknown): VoiceServiceRef | null {
  if (!v || typeof v !== "object") return null
  const o = v as Record<string, unknown>
  if (!isString(o.source_plan_id)) return null
  const snapshot_ids = Array.isArray(o.snapshot_ids)
    ? (o.snapshot_ids as unknown[]).filter(isString)
    : []
  return {
    source_plan_id: o.source_plan_id,
    snapshot_ids,
    category_filter: isString(o.category_filter) ? o.category_filter : null,
    qty: typeof o.qty === "number" && o.qty > 0 ? Math.round(o.qty) : 1,
  }
}

function parseOp(raw: unknown): PlanVoiceOp | null {
  if (!raw || typeof raw !== "object") return null
  const o = raw as Record<string, unknown>
  const op = o.op

  switch (op) {
    case "add_stage": {
      const name = isString(o.name) ? o.name : "Этап"
      const services = Array.isArray(o.services)
        ? (o.services as unknown[]).map(parseServiceRef).filter(Boolean) as VoiceServiceRef[]
        : []
      return {
        op: "add_stage",
        stage_index: typeof o.stage_index === "number" ? o.stage_index : null,
        name,
        doctor_id: isString(o.doctor_id) ? o.doctor_id : null,
        comment: isString(o.comment) ? o.comment : null,
        cash_offer: parseCashOffer(o.cash_offer),
        services,
      }
    }
    case "copy_services": {
      const services = Array.isArray(o.services)
        ? (o.services as unknown[]).map(parseServiceRef).filter(Boolean) as VoiceServiceRef[]
        : []
      return {
        op: "copy_services",
        stage_index: typeof o.stage_index === "number" ? o.stage_index : 0,
        services,
      }
    }
    case "set_installment":
      return { op: "set_installment", config: parseInstallment(o.config) }
    case "set_offer_deadline":
      return { op: "set_offer_deadline", deadline: isString(o.deadline) ? o.deadline : null }
    case "set_plan_cash_offer":
      return { op: "set_plan_cash_offer", offer: parseCashOffer(o.offer) }
    case "set_sharing": {
      const cfg = (o.config && typeof o.config === "object") ? o.config as Record<string, unknown> : o
      return {
        op: "set_sharing",
        config: {
          active: cfg.active === true || cfg.active === "true",
          expires_at: isString(cfg.expires_at) ? cfg.expires_at : null,
          online_payments: cfg.online_payments === true,
          allow_yandex_pay: cfg.allow_yandex_pay === true,
          hide_prices: cfg.hide_prices === true,
        },
      }
    }
    case "set_plan_comment":
      return { op: "set_plan_comment", comment: isString(o.comment) ? o.comment : null }
    case "remove_stage":
      return { op: "remove_stage", stage_index: typeof o.stage_index === "number" ? o.stage_index : 0 }
    case "set_stage_doctor":
      return {
        op: "set_stage_doctor",
        stage_index: typeof o.stage_index === "number" ? o.stage_index : 0,
        doctor_id: isString(o.doctor_id) ? o.doctor_id : null,
      }
    default:
      return null
  }
}

// ─── Тип результата парсинга — несёт и команду и список пропущенных ops ────────

/** Одна пропущенная операция из ответа AI */
export type SkippedOp = {
  /** Порядковый индекс в массиве operations из ответа AI */
  index: number
  /** Значение поля op (может быть undefined если пришла невалидная структура) */
  op_type: string | undefined
  /** Причина пропуска: unknown_op_type | invalid_op_structure | parse_error */
  reason: string
}

/** Результат парсинга: разбитые ops + список того что не удалось разобрать */
export type ParseResult = {
  command: PlanVoiceCommand | null
  /** Ops, которые AI прислал, но мы не смогли разобрать — для UX-предупреждения */
  skipped: SkippedOp[]
}

const VALID_OPS = new Set([
  "add_stage", "copy_services", "set_installment", "set_offer_deadline",
  "set_plan_cash_offer", "set_sharing", "set_plan_comment",
  "remove_stage", "set_stage_doctor",
])

/**
 * Парсит proposal_payload из AMI в ParseResult.
 *
 * Не бросает никогда — graceful degradation:
 * - ops с известным типом но невалидными данными парсятся с дефолтами
 * - ops с неизвестным типом попадают в skipped[]
 * - вызывающий код ДОЛЖЕН показать пользователю предупреждение если skipped.length > 0
 */
export function parsePlanVoiceCommand(raw: unknown): ParseResult {
  const empty: ParseResult = { command: null, skipped: [] }
  if (!raw || typeof raw !== "object") return empty
  const o = raw as Record<string, unknown>
  if (!Array.isArray(o.operations) || o.operations.length === 0) return empty

  const operations: PlanVoiceOp[] = []
  const skipped: SkippedOp[] = []

  ;(o.operations as unknown[]).forEach((item, index) => {
    const rawOp = item as Record<string, unknown> | null
    const op_type = rawOp && typeof rawOp.op === "string" ? rawOp.op : undefined

    if (!op_type || !VALID_OPS.has(op_type)) {
      skipped.push({
        index,
        op_type,
        reason: op_type ? "unknown_op_type" : "invalid_op_structure",
      })
      return
    }

    const parsed = parseOp(item)
    if (parsed) {
      operations.push(parsed)
    } else {
      skipped.push({ index, op_type, reason: "parse_error" })
    }
  })

  if (operations.length === 0) return { command: null, skipped }

  return {
    command: {
      schema_version: "1",
      summary: isString(o.summary) ? o.summary : "Голосовая команда",
      operations,
    },
    skipped,
  }
}

// ─── Человекочитаемые описания операций ──────────────────────────────────────

export function opToHuman(op: PlanVoiceOp): string {
  switch (op.op) {
    case "add_stage": {
      const parts = [`Добавить этап «${op.name}»`]
      if (op.comment) parts.push(`(${op.comment.slice(0, 60)})`)
      if (op.services.length > 0) parts.push(`+ ${op.services.length} группа услуг`)
      return parts.join(" ")
    }
    case "copy_services":
      return `Скопировать ${op.services.length} группу услуг в этап ${op.stage_index + 1}`
    case "set_installment":
      if (!op.config) return "Убрать рассрочку"
      return `Рассрочка ${op.config.months} мес. с ${op.config.start_date}${op.config.rate_pct > 0 ? ` · ${op.config.rate_pct}%` : ""}`
    case "set_offer_deadline":
      if (!op.deadline) return "Убрать таймер оффера"
      return `Таймер оффера до ${op.deadline.slice(0, 10)}`
    case "set_plan_cash_offer":
      if (!op.offer) return "Убрать спец. цену"
      return `Спец. цена за наличные: ${Math.round(op.offer.price_minor / 100).toLocaleString("ru-RU")} ₽${op.offer.label ? ` (${op.offer.label})` : ""}`
    case "set_sharing":
      return op.config.active
        ? `Активировать шеринг${op.config.expires_at ? ` · до ${op.config.expires_at.slice(0, 10)}` : ""}${op.config.online_payments ? " · оплата онлайн" : ""}`
        : "Деактивировать шеринг"
    case "set_plan_comment":
      return op.comment ? `Комментарий: «${op.comment.slice(0, 80)}»` : "Убрать комментарий"
    case "remove_stage":
      return `Удалить этап ${op.stage_index + 1}`
    case "set_stage_doctor":
      return `Врач этапа ${op.stage_index + 1}: ${op.doctor_id ?? "не выбран"}`
  }
}
